/**
 * Quasar prevent-scroll registers a non-passive `window` wheel listener on
 * Mac desktop (see quasar/src/utils/scroll/prevent-scroll.js). That listener
 * calls preventDefault when no overflow scroller is in the event path — which
 * is the normal document-scroll case for our shell pages.
 *
 * WEEKLY REGRESSION: clearing only `q-body--prevent-scroll` / body pin styles
 * leaves this listener registered. Trackpad/wheel scroll stays dead on Mac
 * even though the CSS lock is gone. Capture + gate the listener so leaked
 * locks cannot freeze /links, /audits, /files, etc.
 */

import { shouldClearLeakedBodyScrollLock } from '~/utils/scroll-lock-overlay';

type WheelListener = EventListenerOrEventListenerObject;

interface TrackedWheel {
  listener: WheelListener;
  wrapped: EventListener;
  options: boolean | AddEventListenerOptions | undefined;
}

const tracked: TrackedWheel[] = [];

let installed = false;
let preventScrollFn: ((state: boolean) => void) | null = null;

function isNotPassive(options: boolean | AddEventListenerOptions | undefined): boolean {
  return typeof options === 'object' && options !== null && options.passive === false;
}

function normalizeListener(listener: WheelListener): EventListener {
  if (typeof listener === 'function') {
    return listener;
  }
  return event => listener.handleEvent(event);
}

/** Bind Quasar's shared preventScroll (same module instance as QDrawer/QDialog when available). */
export function bindQuasarPreventScroll(fn: (state: boolean) => void): void {
  preventScrollFn = fn;
}

export function hasCapturedQuasarMacWheelLocks(): boolean {
  return tracked.length > 0;
}

/**
 * Install once on the client before Quasar mounts drawers/dialogs.
 * Wraps non-passive window wheel listeners so they only prevent scroll while
 * a real overlay is open — leaked listeners become no-ops.
 */
export function installQuasarMacWheelLockGuard(): void {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  const originalAdd = window.addEventListener.bind(window);
  const originalRemove = window.removeEventListener.bind(window);

  window.addEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ) => {
    if (type === 'wheel' && listener && isNotPassive(options)) {
      const original = normalizeListener(listener);
      const wrapped: EventListener = event => {
        if (shouldClearLeakedBodyScrollLock(document, window.innerWidth)) {
          return;
        }
        return original.call(window, event);
      };
      tracked.push({ listener, wrapped, options });
      return originalAdd(type, wrapped, options);
    }
    return originalAdd(type, listener as EventListenerOrEventListenerObject, options);
  }) as typeof window.addEventListener;

  window.removeEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions
  ) => {
    if (type === 'wheel' && listener) {
      const idx = tracked.findIndex(entry => entry.listener === listener);
      if (idx >= 0) {
        // cas-2853: `idx >= 0` guarantees splice returns exactly one element, but
        // noUncheckedIndexedAccess types the destructured value as possibly
        // undefined. The guard is a type-level formality — when it somehow does
        // not hold, falling through to the untracked removal below is the same
        // thing this function does for any listener it never wrapped.
        const [entry] = tracked.splice(idx, 1);
        if (entry) {
          return originalRemove(
            type,
            entry.wrapped,
            (entry.options ?? options) as EventListenerOptions
          );
        }
      }
    }
    return originalRemove(type, listener as EventListenerOrEventListenerObject, options);
  }) as typeof window.removeEventListener;
}

/** Remove any captured Mac wheel locks (safe if Quasar already removed them). */
export function removeCapturedQuasarMacWheelLocks(): boolean {
  if (typeof window === 'undefined' || tracked.length === 0) {
    return false;
  }

  // Call through the patched removeEventListener with Quasar's original
  // listener ref so the wrapper is unregistered. Do not pop first — the
  // patch splices `tracked` when it matches.
  const entries = tracked.slice();
  for (const entry of entries) {
    window.removeEventListener('wheel', entry.listener, entry.options as EventListenerOptions);
  }
  return entries.length > 0;
}

/**
 * Decrement Quasar's internal preventScroll registry when we share its module
 * instance (production source imports). No-ops when the client bundle inlined
 * a separate copy — Mac wheel capture covers that path.
 */
export function releaseQuasarPreventScrollRegistry(maxReleases = 5): boolean {
  if (!preventScrollFn || typeof window === 'undefined') {
    return false;
  }

  let released = false;
  for (let i = 0; i < maxReleases; i += 1) {
    preventScrollFn(false);
    released = true;
    if (!(document as Document & { qScrollPrevented?: boolean }).qScrollPrevented) {
      break;
    }
  }
  return released;
}
